import React, {useEffect} from "react";
import {
    Column,
    ColumnType,
    FixedSelectionFilter,
    FreeTextFilter,
    GetParams,
    IMAGES,
    KButton,
    KBUTTON_SIZE,
    KBUTTON_VARIANT,
    KCard,
    KContainer,
    KSpace,
    KSpinner,
    KSpinnerSize,
    KTableLoader,
    Table
} from "@kopjra/uikit";
import {Credential, ProductCredits} from "../types/users";
import {I18n, Localize, Translate} from "react-redux-i18n";
import {tableGetParams, tableHasFilter} from "../utils/commons";
import {Alert, Col, Row} from "react-bootstrap";
import {
    AppStatusToColorMap,
    AppStatusToIconMap,
    AppStatusToTimestampStatusMap,
    TimestampAppStatus,
    TimestampStatusAppStatusMap
} from "../types/signatures";
import {Timestamp} from "../types/timestamps";
import {TimestampFile} from "./TimestampFile";
import {KCredit, KCreditColor} from "@kopjra/uikit/dist/src/lib/KCredit";
import {BuyCTA} from "./helpers/BuyCTA";

export interface StateProps {
    apiKeys?: Credential[];
    timestamps?: Timestamp[];
    total: number;
    stats?: ProductCredits;
    isAllowed: boolean;
}

export interface DispatchProps {
    onGetTimestamps: (q: GetParams) => Promise<void>;
    onGoToApiKeys: () => void;
    onGoToDocumentation: () => void;
    onDoTimestamp: (file: File, trusted: boolean) => Promise<void>;
    onGetUserCredits: () => Promise<void>;
    onResetTimestamps: () => void;
}

export interface InnerProps {
}

export type Props = StateProps & DispatchProps & InnerProps;

const tableName = "timestamps";

export const Timestamps: React.FC<Props> = ({onResetTimestamps, onGetUserCredits, isAllowed, onDoTimestamp, timestamps, onGetTimestamps, stats, total, onGoToApiKeys, onGoToDocumentation, apiKeys}) => {
    useEffect(() => {
        (async () => {
            await onGetTimestamps(tableGetParams(tableName));
        })().catch();
    }, [onGetTimestamps]);

    return (
        <KContainer className="text-center">
            {(apiKeys && timestamps && stats) ? (timestamps.length || tableHasFilter(tableName)) ? (
                <KCard header={<><Translate value="interaction.timestampTitle"/></>} actions={[
                    {element: <i className="fal fa-redo"/>, action: async () => {onResetTimestamps(); await Promise.all([onGetUserCredits(), onGetTimestamps(tableGetParams(tableName))]);}}
                ]}>
                    <Row>
                        <Col md={3} className="text-center">
                            <KCredit infinite={stats.availableQty === -1} remaining={stats.availableQty} total={stats.subscribedQty} labelText={<Translate value="interaction.tsAvailable"/>}/>
                        </Col>
                        <Col md={3} className="text-center">
                            <KCredit infinite={stats.consumedQtyExtra === -1} remaining={stats.consumedQtyExtra} labelText={<Translate value="interaction.tsOverQuota"/>}/>
                        </Col>
                        <Col md={3} className="text-center">
                            <KCredit color={KCreditColor.SUCCESS} infinite={stats.consumedTotal === -1} remaining={stats.consumedTotal} labelText={<Translate value="interaction.tsTotalUsed"/>}/>
                        </Col>
                    </Row>
                    {stats.availableQty < 1 && stats.availableQty !== -1 ? (
                        <>
                            <KSpace spaces={1}/>
                            <Row>
                                <Col md={6}>
                                    <p><Alert variant={"warning"}><Translate value={"interaction.empty"} dangerousHTML={true}/></Alert></p>
                                </Col>
                            </Row>
                        </>
                    ) : null}
                    <KSpace spaces={2}/>
                    <Row>
                        <TimestampFile onDoTimestamp={async (file, trusted) => {
                            await onDoTimestamp(file, trusted);
                            await onGetTimestamps(tableGetParams(tableName));
                        }}/>
                    </Row>
                    <KSpace spaces={1}/>
                    <Table
                        id={tableName}
                        data={timestamps}
                        total_count={total}
                        loaderFunc={async (q: GetParams) => onGetTimestamps(q)}
                        keyField={"id"}
                        waiter={<KSpinner size={KSpinnerSize.xl}/>}
                        filterDefinition={[
                            new FixedSelectionFilter("trusted", <Translate value="interaction.table.filter.trusted"/>,
                                [{value: "true", label: I18n.t("generic.true")}, {value: "false", label: I18n.t("generic.false")}],
                            ),
                            new FreeTextFilter("fileName", <Translate value="interaction.table.filter.fileName"/>),
                            new FixedSelectionFilter("currentStatus", <Translate value="interaction.table.filter.currentStatus"/>,
                                Object.values(TimestampAppStatus).map((appStatus) => ({
                                    label: I18n.t(`interaction.table.appStatus.${appStatus}` as any),
                                    value: AppStatusToTimestampStatusMap[appStatus],
                                })),
                            ),
                        ]}
                    >
                        <Column colid={"createdAt"} type={ColumnType.GENERIC} name="interaction.table.createdAt" sort="createdAt" colspan={2} classes="text-left"
                                render={(datum: Timestamp) => <span className={datum.deletedAt ? "text-decoration-line-through text-lightish" : ""}><Localize value={datum.createdAt} dateFormat={"kptable.date.long"}/></span>}
                        />
                        <Column colid={"trusted"} type={ColumnType.GENERIC} name="interaction.table.trusted" sort="trusted" colspan={2} classes="text-left"
                                render={(datum: Timestamp) => <Translate className={datum.deletedAt ? "text-decoration-line-through text-lightish" : ""} value={`generic.${datum.trusted}` as any} />}
                        />
                        <Column colid={"fileName"} type={ColumnType.GENERIC} name="interaction.table.fileName" sort="fileName" colspan={3} classes="text-left"
                                render={(datum: Timestamp) => <span className={datum.deletedAt ? "text-decoration-line-through text-lightish" : ""}>{datum.fileName}</span>}
                        />
                        <Column colid={"currentStatus"} type={ColumnType.GENERIC} name="interaction.table.currentStatus" sort="currentStatus" colspan={1} classes="text-left nowrap"
                                render={(datum: Timestamp) => {
                                    const appStatus = TimestampStatusAppStatusMap[datum.currentStatus];
                                    return <>
                                        <i className={`fal fa-${AppStatusToIconMap[appStatus]} text-${AppStatusToColorMap[appStatus]}`}/>
                                        <Translate className={`tableStatus ${datum.deletedAt ? "text-decoration-line-through text-lightish" : ""}`} value={`interaction.table.appStatus.${appStatus}` as any}/>
                                    </>
                                }}
                        />
                        <Column colid={"timestampRequestedAt"} type={ColumnType.DATETIME} name="interaction.table.timestampRequestedAt" sort="timestampRequestedAt" colspan={3} visible={false} classes="text-left"
                                render={(datum: Timestamp) => <span className={datum.deletedAt ? "text-decoration-line-through text-lightish" : ""}>{datum.timestampRequestedAt && <Localize value={datum.timestampRequestedAt} dateFormat="kptable.date.long"/>}</span>}
                        />
                    </Table>
                </KCard>
            ) : isAllowed ? (
                <>
                    <KSpace spaces={3}/>
                    <img src={IMAGES["web-trust"].still.large.dark} height={165} alt={"Web Trust"}/>
                    <KSpace/>
                    <h4><Translate value="interaction.noTimestamps"/></h4>
                    <KSpace/>
                    {apiKeys.length ? (
                        <h4><Translate value="interaction.withKeys"/></h4>
                    ) : (
                        <h4><Translate value="interaction.noKeys"/></h4>
                    )}
                    <KSpace/>
                    <div>
                        {!apiKeys.length ? (
                            <>
                                <KButton
                                    text={<><i className="fal fa-angle-double-right"/> <Translate value="user.apiKeys.title"/></>}
                                    variant={KBUTTON_VARIANT.primary}
                                    size={KBUTTON_SIZE.nm}
                                    onClick={() => onGoToApiKeys()}
                                />
                                <KSpace v={true} spaces={2}/>
                            </>
                        ) : null}
                        <KButton
                            text={<><i className="fal fa-angle-double-right"/> <Translate value="user.documentation"/></>}
                            variant={KBUTTON_VARIANT.primary}
                            size={KBUTTON_SIZE.nm}
                            onClick={() => onGoToDocumentation()}
                        />
                    </div>
                    <KSpace spaces={2}/>
                    <h4><Translate value={"generic.or"}/></h4>
                    <KSpace/>
                    <TimestampFile onDoTimestamp={async (file, trusted) => {
                        await onDoTimestamp(file, trusted);
                        await onGetTimestamps(tableGetParams(tableName));
                    }}/>
                    {/*<KSpace spaces={2}/>*/}
                    {/*<h4><a className="link" href={`mailto:info@kopjra.com?subject=${I18n.t("home.contactSubject")}&body=${I18n.t("home.contactBody")}`} target="_blank" rel="noopener noreferrer"><Translate value="home.contactus"/></a></h4>*/}
                </>
            ) : (
                <BuyCTA/>
            ) : (
                <KCard header={<Translate value="interaction.timestampTitle"/>}>
                    <KTableLoader/>
                </KCard>
            )}
        </KContainer>
    );
}
